import { Component } from '@theme/component';
import {
  buildPayload,
  createLeadEventId,
  fireConfirmationRequiredEvent,
  fireConversionEvents,
  parseContactMethod,
  submitLead,
} from '@theme/pharmacy-lead-engine';

class PharmacyLeadForm extends Component {
  #isSubmitting = false;
  #leadEventId = createLeadEventId();
  #lastPayload = null;
  #defaultErrorTitle = 'We could not send your request';
  #formElement = null;

  connectedCallback() {
    super.connectedCallback();

    if (window.location.pathname.includes('/for-pharmacies')) {
      document.title = 'For Pharmacies - Tara USA';
    }

    this.#formElement = this.querySelector('form.tara-b2b__form');
    this.#upgradeLegacyContactFields();
    this.updatedCallback();
    this.#formElement?.addEventListener('submit', this.#handleNativeSubmit);
    this.#formElement?.addEventListener('input', this.#handleFieldInteraction);
    this.#formElement?.addEventListener('change', this.#handleFieldInteraction);

    this.#hideError();
    this.#getErrorTitleElement()?.replaceChildren();
    this.#getErrorTextElement()?.replaceChildren();
  }

  disconnectedCallback() {
    this.#formElement?.removeEventListener('submit', this.#handleNativeSubmit);
    this.#formElement?.removeEventListener('input', this.#handleFieldInteraction);
    this.#formElement?.removeEventListener('change', this.#handleFieldInteraction);
    this.#formElement = null;
    super.disconnectedCallback();
  }

  handleSubmit(dataOrEvent, event) {
    const submitEvent = event ?? dataOrEvent;
    submitEvent?.preventDefault();

    if (this.#isSubmitting) return;
    if (this.refs.honeypot?.value) return;

    const validation = this.#validateForm();
    if (!validation.valid) {
      this.#showErrorWithTitle(validation.title, validation.message);
      validation.field?.focus();
      return;
    }

    this.#submit();
  }

  handleDismissError() {
    this.#hideError();
  }

  handleConfirmExisting() {
    this.#submitConfirmation('use_existing');
  }

  handleConfirmSubmitted() {
    this.#submitConfirmation('use_submitted');
  }

  #validateForm() {
    this.#clearValidationState();

    const pharmacyName = this.refs.pharmacyName?.value.trim() ?? '';
    const contactMethod = parseContactMethod(this.refs.contactMethod?.value ?? '');
    const consentPrivacy = this.refs.consentPrivacy?.checked ?? false;

    if (!pharmacyName) {
      return this.#invalidateField(
        this.refs.pharmacyName,
        'Pharmacy name required',
        'Enter the pharmacy name.',
        'We need the pharmacy name to review your request.',
      );
    }

    if (contactMethod.type === 'empty') {
      return this.#invalidateField(
        this.refs.contactMethod,
        'Contact method required',
        'Enter a professional email or phone number.',
        'Leave a professional email or phone number so we can reply.',
      );
    }

    if (contactMethod.type === 'invalid_email') {
      return this.#invalidateField(
        this.refs.contactMethod,
        'Check the email address',
        'The email address does not look valid. Please enter a valid email.',
        'The email address does not look valid. Please enter a valid email.',
      );
    }

    if (contactMethod.type === 'invalid_phone') {
      return this.#invalidateField(
        this.refs.contactMethod,
        'Check the phone number',
        'The phone number does not look valid. Please enter a valid phone number.',
        'The phone number does not look valid. Please enter a valid phone number.',
      );
    }

    if (!consentPrivacy) {
      return this.#invalidateField(
        this.refs.consentPrivacy,
        'Confirmation required',
        'You must accept the privacy policy.',
        'You must accept the privacy notice before sending your request.',
      );
    }

    return { valid: true };
  }

  #invalidateField(field, title, browserMessage, inlineMessage) {
    field?.setAttribute('aria-invalid', 'true');
    field?.setCustomValidity(browserMessage);
    field?.reportValidity();

    return {
      valid: false,
      field,
      title,
      message: inlineMessage,
    };
  }

  #clearValidationState() {
    [
      this.refs.pharmacyName,
      this.refs.contactMethod,
      this.refs.consentPrivacy,
    ].forEach((field) => {
      field?.removeAttribute('aria-invalid');
      field?.setCustomValidity('');
    });
  }

  async #submit(overrides = {}) {
    this.#isSubmitting = true;
    this.#setLoadingState(true);
    this.#hideError();

    const endpointBase = this.dataset.crmEndpoint;
    if (!endpointBase) {
      this.#showError('We cannot prepare your request right now. Please try again in a few minutes.');
      this.#setLoadingState(false);
      this.#isSubmitting = false;
      return;
    }

    this.#lastPayload = buildPayload(this.refs, '', 'v2', overrides);

    try {
      const result = await submitLead(endpointBase, this.#lastPayload);
      this.#handleResponse(result);
    } catch (error) {
      this.#showError(this.#getErrorMessage(error));
      this.#setLoadingState(false);
    } finally {
      this.#isSubmitting = false;
    }
  }

  async #submitConfirmation(decision) {
    if (this.#isSubmitting || !this.#lastPayload) return;

    const candidateId = this.refs.confirmationPanel?.dataset.candidateId || null;
    const overrides = {
      confirm_near_match: true,
      match_decision: decision,
      confirmed_pharmacy_id: decision === 'use_existing' ? candidateId : null,
    };

    if (this.refs.confirmationPanel) this.refs.confirmationPanel.hidden = true;
    await this.#submit(overrides);
  }

  #handleResponse(result) {
    this.#setLoadingState(false);

    if (result?.status === 'needs_confirmation') {
      fireConfirmationRequiredEvent(this.#leadEventId);
      this.#showConfirmation(result?.candidate || {});
      return;
    }

    if (result?.status === 'error' || result?.error) {
      this.#showErrorWithTitle(
        this.#defaultErrorTitle,
        typeof result?.error === 'string'
          ? `We could not process your request: ${result.error}`
          : 'We could not process your request. Please try again in a few minutes.',
      );
      return;
    }

    fireConversionEvents(this.#leadEventId, this.#lastPayload || {});
    this.#showSuccess();
  }

  #showConfirmation(candidate) {
    if (!this.refs.confirmationPanel) {
      this.#showSuccess();
      return;
    }

    this.refs.confirmationPanel.dataset.candidateId = candidate?.pharmacy_id || '';

    const fields = {
      pharmacy_name: candidate?.pharmacy_name,
      city: candidate?.city,
      postal_code: candidate?.postal_code,
      phone: candidate?.phone,
      email: candidate?.email,
      website: candidate?.website,
    };

    Object.entries(fields).forEach(([key, value]) => {
      const target = this.refs.confirmationPanel.querySelector(`[data-candidate-field="${key}"]`);
      if (target) target.textContent = value || 'Not available';
    });

    if (this.refs.formContainer) this.refs.formContainer.hidden = true;
    this.refs.confirmationPanel.hidden = false;
    this.refs.confirmationPanel.focus();
  }

  #showSuccess() {
    if (this.refs.formContainer) this.refs.formContainer.hidden = true;
    if (this.refs.confirmationPanel) this.refs.confirmationPanel.hidden = true;
    if (this.refs.successMessage) {
      this.refs.successMessage.hidden = false;
      this.refs.successMessage.focus();
    }
  }

  #getErrorMessage(error) {
    if (error?.code === 'network_failure') {
      return 'We could not send your request right now. Check your connection and try again.';
    }

    const backendMessage = error?.response?.error || error?.response?.message;
    if (backendMessage) {
      return `We could not process your request: ${backendMessage}`;
    }

    return 'We could not send your request right now. Please try again in a few minutes.';
  }

  #setLoadingState(loading) {
    if (this.refs.submitButton) this.refs.submitButton.disabled = loading;
    if (this.refs.submitLabel) this.refs.submitLabel.hidden = loading;
    if (this.refs.submitLoading) this.refs.submitLoading.hidden = !loading;
    this.toggleAttribute('aria-busy', loading);
  }

  #showError(message) {
    this.#showErrorWithTitle(this.#defaultErrorTitle, message);
  }

  #showErrorWithTitle(title, message) {
    if (this.#getErrorTitleElement()) this.#getErrorTitleElement().textContent = title;
    if (this.#getErrorTextElement()) this.#getErrorTextElement().textContent = message;
    if (this.refs.errorMessage) this.refs.errorMessage.hidden = false;
    this.refs.errorMessage?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  #hideError() {
    if (this.refs.errorMessage) this.refs.errorMessage.hidden = true;
    if (this.#getErrorTitleElement()) this.#getErrorTitleElement().textContent = '';
    if (this.#getErrorTextElement()) this.#getErrorTextElement().textContent = '';
  }

  #getErrorTitleElement() {
    return this.refs.errorTitle || this.querySelector('.tara-b2b__status-title');
  }

  #getErrorTextElement() {
    return this.refs.errorText || this.querySelector('.tara-b2b__status-text');
  }

  #upgradeLegacyContactFields() {
    if (this.querySelector('[ref="contactMethod"]')) return;

    const phoneInput = this.querySelector('[ref="phone"]');
    const emailInput = this.querySelector('[ref="email"]');
    if (!(phoneInput instanceof HTMLInputElement) || !(emailInput instanceof HTMLInputElement)) return;

    const legacyRow = phoneInput.closest('.tara-b2b__row');
    if (!(legacyRow instanceof HTMLElement)) return;

    const field = document.createElement('div');
    field.className = 'tara-b2b__field';

    const label = document.createElement('label');
    label.className = 'tara-b2b__label';
    label.htmlFor = `PharmacyLeadContactMethod-${this.id || 'legacy'}`;
    label.innerHTML = 'Professional email or phone <span aria-hidden="true">*</span>';

    const input = document.createElement('input');
    input.id = label.htmlFor;
    input.className = 'tara-b2b__input';
    input.type = 'text';
    input.name = 'contact_method';
    input.setAttribute('ref', 'contactMethod');
    input.autocomplete = 'off';
    input.autocapitalize = 'off';
    input.spellcheck = false;
    input.placeholder = 'you@email.com or 555 123 4567';

    const emailValue = emailInput.value.trim();
    const phoneValue = phoneInput.value.trim();
    input.value = emailValue || phoneValue;

    field.append(label, input);
    legacyRow.before(field);
    legacyRow.hidden = true;
  }

  #handleNativeSubmit = (event) => {
    this.handleSubmit(event);
  };

  #handleFieldInteraction = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;

    target.removeAttribute('aria-invalid');
    target.setCustomValidity('');

    if (!this.#isCurrentValidationError()) {
      this.#hideError();
    }
  };

  #isCurrentValidationError() {
    const pharmacyName = this.refs.pharmacyName?.value.trim() ?? '';
    const contactMethod = parseContactMethod(this.refs.contactMethod?.value ?? '');
    const consentPrivacy = this.refs.consentPrivacy?.checked ?? false;

    if (!pharmacyName) return true;
    if (contactMethod.type === 'empty') return true;
    if (contactMethod.type === 'invalid_email') return true;
    if (contactMethod.type === 'invalid_phone') return true;
    if (!consentPrivacy) return true;

    return false;
  }
}

customElements.define('pharmacy-lead-form', PharmacyLeadForm);

export default PharmacyLeadForm;
